import React, { useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { HelmetProvider } from 'react-helmet-async';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Home } from './components/Home';
import { Game } from './components/Game';
import { ArchivePage } from './components/ArchivePage';
import { NewsPage } from './components/NewsPage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { AboutPage } from './components/AboutPage';
import { HowToPlayPage } from './components/HowToPlayPage';
import { NotFoundPage } from './components/NotFoundPage';
import { YouPage } from './components/YouPage';
import { NarrowPage } from './components/SiteChrome';
import { useUser } from './hooks/useUser';
import { resolveArchiveParam } from './lib/archivePaths';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 font-sans">
      <main className="max-w-6xl mx-auto p-4">{children}</main>
      <Analytics />
    </div>
  );
}

function PlayRoute({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const forceOpenStats = Boolean((location.state as { openStats?: boolean } | null)?.openStats);

  return (
    <Game
      userId={userId}
      archiveDate={null}
      forceOpenStats={forceOpenStats}
      onStatsOpened={() => {
        if (forceOpenStats) navigate('/play', { replace: true, state: {} });
      }}
    />
  );
}

function ArchivePuzzleRoute({ userId }: { userId: string }) {
  const { slug } = useParams<{ slug: string }>();
  const [resolved, setResolved] = useState<{
    param: string;
    date: string;
    path: string;
  } | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!slug) {
        setMissing(true);
        return;
      }
      const hit = await resolveArchiveParam(slug);
      if (cancelled) return;
      if (!hit) {
        setMissing(true);
        return;
      }
      setMissing(false);
      setResolved({ param: slug, date: hit.topic.play_date, path: hit.path });
    }
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (missing) return <Navigate to="/archive" replace />;
  if (!resolved) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }
  // Only redirect once `resolved` belongs to the current URL; right after a
  // prev/next click it still describes the previous topic.
  if (slug && resolved.param === slug && `/archive/${slug}` !== resolved.path) {
    return <Navigate to={resolved.path} replace />;
  }

  return <Game userId={userId} archiveDate={resolved.date} forceOpenStats={false} />;
}

function UserGate({
  userId,
  loading,
  children,
}: {
  userId: string | null;
  loading: boolean;
  children: React.ReactNode;
}) {
  if (loading || !userId) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }
  return <>{children}</>;
}

function AppRoutes({ userId, loading }: { userId: string | null; loading: boolean }) {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path="/play"
        element={
          <UserGate userId={userId} loading={loading}>
            <NarrowPage>
              <PlayRoute userId={userId!} />
            </NarrowPage>
          </UserGate>
        }
      />
      <Route path="/archive" element={<ArchivePage />} />
      <Route
        path="/archive/:slug"
        element={
          <UserGate userId={userId} loading={loading}>
            <NarrowPage>
              <ArchivePuzzleRoute userId={userId!} />
            </NarrowPage>
          </UserGate>
        }
      />
      <Route path="/news" element={<NewsPage />} />
      <Route
        path="/you"
        element={
          <UserGate userId={userId} loading={loading}>
            <YouPage userId={userId!} />
          </UserGate>
        }
      />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-to-play" element={<HowToPlayPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const { userId, loading } = useUser();

  useEffect(() => {
    const prefersDark =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (localStorage.theme === 'dark' || (!('theme' in localStorage) && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell>
          <AppRoutes userId={userId} loading={loading} />
        </Shell>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
